import { useSim } from "@/context/SimContext";
import { toast } from "sonner";
import {
  Play, AlertTriangle, HelpCircle, ShuffleIcon, MapPin, ShieldAlert, RotateCcw, X,
} from "lucide-react";

const SCENARIOS = [
  { key: "normal", label: "Normal Operation", desc: "High confidence · auto execute", icon: Play, tone: "emerald" },
  { key: "low_confidence", label: "Low Confidence Event", desc: "Confidence below threshold", icon: AlertTriangle, tone: "amber" },
  { key: "unknown_object", label: "Unknown Object", desc: "Classification UNKNOWN", icon: HelpCircle, tone: "amber" },
  { key: "wrong_classification", label: "Wrong Classification", desc: "Possible misclassification", icon: ShuffleIcon, tone: "amber" },
  { key: "wrong_destination", label: "Wrong Destination", desc: "Incorrect placement bin", icon: MapPin, tone: "amber" },
  { key: "safety_event", label: "Safety Event", desc: "Requires explicit review", icon: ShieldAlert, tone: "red" },
];

const TONE = {
  emerald: "hover:border-emerald-500 text-emerald-400",
  amber: "hover:border-amber-500 text-amber-400",
  red: "hover:border-red-500 text-red-400",
};

export default function DemoDrawer({ open, onClose }) {
  const { trigger } = useSim();

  const run = async (key, label) => {
    try {
      await trigger(key);
      toast.success(`Scenario triggered: ${label}`);
    } catch {
      toast.error("Failed to trigger scenario");
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />
      <div
        data-testid="demo-drawer"
        className={`fixed top-0 right-0 h-full w-[380px] bg-[#070E1A] border-l border-slate-800 z-50 transition-transform duration-300 flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-800">
          <div>
            <div className="font-display text-base font-bold">Demo Control Panel</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Presenter Scenarios</div>
          </div>
          <button onClick={onClose} data-testid="close-demo-drawer" className="text-slate-500 hover:text-slate-200">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <p className="text-xs text-slate-400 leading-relaxed mb-2">
            Trigger Human-in-the-Loop scenarios on demand during a live demonstration.
          </p>
          {SCENARIOS.map(({ key, label, desc, icon: Icon, tone }) => (
            <button
              key={key} onClick={() => run(key, label)} data-testid={`scenario-${key}`}
              className={`w-full flex items-center gap-3 p-3.5 rounded-md bg-[#0A1128] border border-slate-800 text-left transition-colors ${TONE[tone]}`}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-slate-100">{label}</span>
                <span className="block text-xs text-slate-400">{desc}</span>
              </span>
            </button>
          ))}
          <button
            onClick={() => run("reset", "Reset Simulation")} data-testid="scenario-reset"
            className="w-full flex items-center gap-3 p-3.5 rounded-md bg-slate-800/40 border border-slate-700 text-left hover:border-blue-500 transition-colors text-slate-300"
          >
            <RotateCcw className="h-5 w-5 shrink-0" />
            <span className="flex-1">
              <span className="block text-sm font-medium">Reset Simulation</span>
              <span className="block text-xs text-slate-400">Restart NGX Core session</span>
            </span>
          </button>
        </div>
      </div>
    </>
  );
}
